import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../api/client';
import './Settings.css';

const TABS = ['Profile','Company','Security','Backup','Notifications','Billing','Roles'];

export default function Settings() {
  const { theme }  = useTheme();
  const { user }   = useAuth();
  const { company, update } = useCompany();
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(urlTab || 'Profile');
  useEffect(() => { if (urlTab) setTab(urlTab); }, [urlTab]);
  const [saved,  setSaved]  = useState(false);
  const [co,     setCo]     = useState(() => {
    try {
      const profile = JSON.parse(localStorage.getItem('ryzenos_company_profile') || 'null');
      const onboarding = JSON.parse(localStorage.getItem('ryzenos_company') || 'null');
      return profile || onboarding || company;
    } catch { return company; }
  });

  /* Profile state -- controlled inputs */
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ryzenos_user_profile') || 'null') || {name:user.name||'',email:user.email||'',phone:'',dept:user.dept||''}; } catch { return {name:user.name||'',email:user.email||'',phone:'',dept:user.dept||''}; }
  });
  const setP = (k,v) => setProfile(p=>({...p,[k]:v}));
  const setCof = (k,v) => setCo(p=>({...p,[k]:v}));
  const setCoBank = (k,v) => setCo(p=>({...p, bank:{...(p.bank||{}), [k]:v}}));
  const coLogoRef = useRef();
  const onCoLogo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCof('logo', reader.result);
    reader.readAsDataURL(f);
  };

  /* Security state */
  const [sec, setSec] = useState({cur:'',newPw:'',confirm:''});
  const [secMsg, setSecMsg] = useState('');
  const setS = (k,v) => setSec(p=>({...p,[k]:v}));

  const showSaved = () => { setSaved(true); setTimeout(()=>setSaved(false),2000); };

  const saveProfile = () => {
    localStorage.setItem('ryzenos_user_profile', JSON.stringify(profile));
    showSaved();
  };

  const saveCompany = () => {
    update(co);
    localStorage.setItem('ryzenos_company_profile', JSON.stringify(co));
    showSaved();
  };

  const updatePassword = () => {
    if (!sec.cur) { setSecMsg('Enter your current password'); return; }
    if (sec.newPw.length < 6) { setSecMsg('New password must be at least 6 characters'); return; }
    if (sec.newPw !== sec.confirm) { setSecMsg('Passwords do not match'); return; }
    api.auth.changePassword(sec.newPw)
      .then(() => {
        setSecMsg('Password updated successfully');
        setSec({cur:'',newPw:'',confirm:''});
        setTimeout(()=>setSecMsg(''),3000);
      })
      .catch(err => setSecMsg(err.message || 'Failed to update password'));
  };

  /* Billing state */
  const PLANS = [
    { id:'starter',  name:'Starter',  price:1499, userLimit:3,  features:['Lead pipeline and follow-ups','Client management','Finance and invoice tracking','Projects and calendar','Up to 3 team members'] },
    { id:'growth',   name:'Growth',   price:2499, userLimit:6,  features:['Everything in Starter','Advanced analytics and reports','Team attendance and tasks','Up to 6 team members','Priority WhatsApp support'] },
    { id:'business', name:'Business', price:6000, userLimit:null, features:['Everything in Growth','Unlimited team members','API access and integrations','Dedicated onboarding'] },
  ];
  const UPGRADE_WHATSAPP = '918129582113';
  const [billing, setBilling] = useState({ plan:'business', status:'active', renewsOn:null, usersUsed:null, internal:true, paymentsEnabled:false });
  const [upgradeMsg, setUpgradeMsg] = useState('');
  const [payMsg, setPayMsg] = useState('');
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    api.billing.get()
      .then(b => setBilling({ ...b, renewsOn: b.expiresAt || null, internal:false }))
      .catch(() => {}); /* internal default until backend responds */
    api.team.list().then(rows => setBilling(p => ({ ...p, usersUsed: rows.length }))).catch(() => {});
  }, []);
  const currentPlan = PLANS.find(p => p.id === billing.plan) || PLANS[2];
  const payNow = async () => {
    setPayMsg('');
    if (!window.Razorpay) { setPayMsg('Payment library not loaded — please refresh and try again.'); return; }
    try {
      setPaying(true);
      const order = await api.billing.order();
      setPaying(false);
      const rzp = new window.Razorpay({
        key: order.keyId, amount: order.amount, currency: order.currency, order_id: order.orderId,
        name: 'RyzenOS', description: `${order.planLabel} plan — 1 month`,
        prefill: { name: user.name || '', email: user.email || '' },
        theme: { color: '#F5A623' },
        handler: async (resp) => {
          try {
            const r = await api.billing.verify(resp);
            setBilling(b => ({ ...b, status:'active', renewsOn: r.expiresAt }));
            setPayMsg('Payment successful — your plan is active until ' + new Date(r.expiresAt).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) + '.');
          } catch (e) { setPayMsg(e.message || 'Payment verification failed.'); }
        },
        modal: { ondismiss: () => setPayMsg('Payment cancelled.') },
      });
      rzp.open();
    } catch (e) { setPaying(false); setPayMsg(e.message || 'Could not start payment.'); }
  };
  const requestUpgrade = (target) => {
    api.billing.requestUpgrade(target.id).catch(() => {});
    setUpgradeMsg(`Upgrade request for ${target.name} sent. We will confirm on WhatsApp shortly.`);
    setTimeout(() => setUpgradeMsg(''), 4000);
    const msg = `Hi Ryzenforge, I would like to upgrade my RyzenOS workspace to the ${target.name} plan (₹${target.price.toLocaleString('en-IN')}/month + GST). Company: ${co?.name || ''}`;
    window.open(`https://wa.me/${UPGRADE_WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  /* Backup state */
  const [backupMsg, setBackupMsg] = useState('');
  const downloadBackup = () => {
    setBackupMsg('Preparing backup...');
    fetch(api.backup.exportUrl(), {
      headers: { Authorization: `Bearer ${api.getToken()}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Backup failed');
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ryzenos-backup-${new Date().toISOString().slice(0,10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setBackupMsg('Backup downloaded.');
        setTimeout(()=>setBackupMsg(''), 3000);
      })
      .catch(() => {
        setBackupMsg('Backup failed. Try again.');
        setTimeout(()=>setBackupMsg(''), 3000);
      });
  };

  return (
    <div className="st-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="st-main">
        <Topbar title="Settings" subtitle="Manage your account and workspace"/>
        <div className="st-body">
          <div className="st-nav">
            {TABS.filter(t => t !== 'Backup' || user.role === 'Super Admin' || user.role === 'Admin').map(t=><div key={t} className={`st-nav-item${tab===t?' active':''}`} onClick={()=>setTab(t)}>{t}</div>)}
          </div>
          <div className="st-content">

            {tab==='Profile' && (
              <div className="st-section">
                <div className="st-sec-title">My Profile</div>
                <div className="st-av-row">
                  <div className="st-av">{user.initials}</div>
                  <div>
                    <div className="st-av-name">{profile.name||user.name}</div>
                    <div className="st-av-role">{user.role}</div>
                  </div>
                </div>
                <div className="st-fields">
                  <div className="st-field-grp"><label className="st-lbl">Name</label><input className="st-inp" value={profile.name} onChange={e=>setP('name',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Email</label><input className="st-inp" value={profile.email} onChange={e=>setP('email',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Phone</label><input className="st-inp" value={profile.phone} placeholder="+91 XXXXX XXXXX" onChange={e=>setP('phone',e.target.value)}/></div>
                  <div className="st-field-grp"><label className="st-lbl">Department</label><input className="st-inp" value={profile.dept} onChange={e=>setP('dept',e.target.value)}/></div>
                </div>
                <div className="st-save-row">
                  <button className="st-save-btn" onClick={saveProfile}>{saved?'✓ Saved':'Save changes'}</button>
                </div>
              </div>
            )}

            {tab==='Company' && (
              <div className="st-section">
                <div className="st-sec-title">Company Profile</div>
                <div className="st-fields">
                  {[
                    {k:'name',    label:'Company name',   placeholder:'Ryzenforge Enterprises LLP'},
                    {k:'tagline', label:'Tagline',         placeholder:'Run your business smarter'},
                    {k:'email',   label:'Business email',  placeholder:'connect@ryzenforge.com'},
                    {k:'phone',   label:'Phone',           placeholder:'+91 81295 82113'},
                    {k:'website', label:'Website',         placeholder:'www.ryzenforge.com'},
                    {k:'gstin',   label:'GSTIN',           placeholder:'22AAAAA0000A1Z5'},
                    {k:'address', label:'Address',         placeholder:'Tower 2, DD Platinum Planet'},
                    {k:'city',    label:'City',            placeholder:'Kochi'},
                    {k:'state',   label:'State',           placeholder:'Kerala'},
                    {k:'pincode', label:'Pincode',         placeholder:'682017'},
                  ].map(f=>(
                    <div key={f.k} className="st-field-grp">
                      <label className="st-lbl">{f.label}</label>
                      <input className="st-inp" placeholder={f.placeholder} value={co[f.k]||''} onChange={e=>setCof(f.k,e.target.value)}/>
                    </div>
                  ))}
                </div>

                <div className="st-sec-title" style={{fontSize:13,margin:'22px 0 12px'}}>Company logo</div>
                <div className="st-logo-row">
                  {co.logo
                    ? <img src={co.logo} alt="logo" className="st-logo-prev"/>
                    : <div className="st-logo-ph">No logo</div>}
                  <input ref={coLogoRef} type="file" accept="image/*" style={{display:'none'}} onChange={onCoLogo}/>
                  <button className="st-save-btn" onClick={()=>coLogoRef.current.click()}>{co.logo?'Change logo':'Upload logo'}</button>
                  {co.logo && <button className="st-cancel-btn" onClick={()=>setCof('logo','')}>Remove</button>}
                </div>

                <div className="st-help-text" style={{fontSize:12,margin:'18px 0 0',opacity:.6}}>Manage your services under Tools &gt; Service Types.</div>

                <div className="st-sec-title" style={{fontSize:13,margin:'22px 0 12px'}}>Bank / payment details</div>
                <div className="st-help-text" style={{fontSize:12,marginBottom:12,opacity:.6}}>These appear as the payment details on invoices you send.</div>
                <div className="st-fields">
                  {[
                    {k:'accName',   label:'Account holder name', placeholder:'As per bank records'},
                    {k:'accNumber', label:'Account number',      placeholder:'000000000000'},
                    {k:'bank',      label:'Bank',                placeholder:'Federal Bank'},
                    {k:'branch',    label:'Branch',              placeholder:'Panampilly Nagar'},
                    {k:'ifsc',      label:'IFSC code',           placeholder:'FDRL0001316'},
                  ].map(f=>(
                    <div key={f.k} className="st-field-grp">
                      <label className="st-lbl">{f.label}</label>
                      <input className="st-inp" placeholder={f.placeholder} value={(co.bank&&co.bank[f.k])||''} onChange={e=>setCoBank(f.k,e.target.value)}/>
                    </div>
                  ))}
                </div>

                <div className="st-save-row">
                  <button className="st-save-btn" onClick={saveCompany}>{saved?'✓ Saved':'Save company profile'}</button>
                </div>
              </div>
            )}

            {tab==='Security' && (
              <div className="st-section">
                <div className="st-sec-title">Security</div>
                <div className="st-fields">
                  <div className="st-field-grp"><label className="st-lbl">Current password</label><input className="st-inp" type="password" value={sec.cur} onChange={e=>setS('cur',e.target.value)} placeholder="••••••••"/></div>
                  <div className="st-field-grp"><label className="st-lbl">New password</label><input className="st-inp" type="password" value={sec.newPw} onChange={e=>setS('newPw',e.target.value)} placeholder="••••••••"/></div>
                  <div className="st-field-grp"><label className="st-lbl">Confirm password</label><input className="st-inp" type="password" value={sec.confirm} onChange={e=>setS('confirm',e.target.value)} placeholder="••••••••"/></div>
                </div>
                {secMsg && <div className={`st-msg ${secMsg.includes('success')?'st-msg-ok':'st-msg-err'}`}>{secMsg}</div>}
                <div className="st-save-row"><button className="st-save-btn" onClick={updatePassword}>Update password</button></div>
              </div>
            )}

            {tab==='Backup' && (
              <div className="st-section">
                <div className="st-sec-title">Backup</div>
                <div className="st-help-text" style={{fontSize:13,marginBottom:16,lineHeight:1.5}}>
                  Download a complete copy of all your CRM data — leads, clients, invoices, expenses, projects, team, calendar events, and notifications — as a JSON file you can store anywhere.
                </div>
                <div className="st-save-row">
                  <button className="st-save-btn" onClick={downloadBackup}>Download Backup Now</button>
                </div>
                {backupMsg && <div className={`st-msg ${backupMsg.includes('downloaded')?'st-msg-ok':backupMsg.includes('failed')?'st-msg-err':''}`} style={{marginTop:10}}>{backupMsg}</div>}

                <div style={{marginTop:28,paddingTop:20,borderTop:'.5px solid rgba(128,128,128,.15)'}}>
                  <div className="st-help-title" style={{fontSize:13,fontWeight:600,marginBottom:6}}>Automatic weekly backups</div>
                  <div className="st-help-text" style={{fontSize:12,lineHeight:1.5}}>
                    The server automatically backs up the entire database every Sunday at 2:00 AM and keeps the last 8 weeks of backups. This runs independently of the app, so your data is protected even if something goes wrong.
                  </div>
                </div>
              </div>
            )}

            {tab==='Notifications' && (
              <div className="st-section">
                <div className="st-sec-title">Notification Preferences</div>
                {[
                  {key:'followup',  label:'Follow-up reminders',   desc:'Get notified when a lead follow-up is due'},
                  {key:'invoice',   label:'Invoice due alerts',     desc:'Get notified when an invoice is overdue'},
                  {key:'team',      label:'Team attendance',        desc:'Daily attendance summary at 10 AM'},
                  {key:'lead',      label:'New lead alerts',        desc:'Notify when a new lead is added'},
                  {key:'task',      label:'Task deadlines',         desc:'Remind 1 day before a task is due'},
                ].map(n => {
                  const stored = JSON.parse(localStorage.getItem('ryzenos_notif_prefs') || '{}');
                  const isOn = stored[n.key] !== false;
                  return (
                    <div key={n.key} className="st-notif-row">
                      <div>
                        <div className="st-notif-lbl">{n.label}</div>
                        <div className="st-notif-desc">{n.desc}</div>
                      </div>
                      <div className={`st-toggle${isOn?' on':''}`} onClick={()=>{
                        const p = JSON.parse(localStorage.getItem('ryzenos_notif_prefs') || '{}');
                        p[n.key] = !isOn;
                        localStorage.setItem('ryzenos_notif_prefs', JSON.stringify(p));
                        showSaved();
                      }}><div className="st-toggle-thumb"/></div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab==='Billing' && (
              <div className="st-section">
                <div className="st-sec-title">Billing</div>

                <div className="st-billing-card">
                  <div className="st-plan-name">RyzenOS {currentPlan.name}</div>
                  <div className="st-plan-price">₹{currentPlan.price.toLocaleString('en-IN')} <span>/month + GST</span></div>
                  <div className="st-plan-status">
                    {billing.status === 'active' ? '✓ Active' : billing.status}
                    {billing.renewsOn ? ` — Renews ${new Date(billing.renewsOn).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}` : billing.internal ? ' — Internal workspace' : ''}
                  </div>
                  {billing.paymentsEnabled && !billing.internal && (
                    <div style={{marginTop:14}}>
                      <button className="st-save-btn" onClick={payNow} disabled={paying}>
                        {paying ? 'Starting…' : `Renew plan — pay ₹${currentPlan.price.toLocaleString('en-IN')}`}
                      </button>
                      {payMsg && <div className="st-msg st-msg-ok" style={{marginTop:12}}>{payMsg}</div>}
                    </div>
                  )}
                </div>

                <div className="st-billing-info">
                  <div className="st-info-row">
                    <span className="st-info-lbl">Team members</span>
                    <span className="st-info-val">
                      {billing.usersUsed != null ? billing.usersUsed : '—'} of {currentPlan.userLimit == null ? 'Unlimited' : currentPlan.userLimit}
                    </span>
                  </div>
                  {currentPlan.features.map(f => (
                    <div key={f} className="st-info-row"><span className="st-info-lbl">Included</span><span className="st-info-val">{f}</span></div>
                  ))}
                </div>

                {currentPlan.id !== 'business' && (
                  <div style={{marginTop:28}}>
                    <div className="st-sec-title" style={{fontSize:13,marginBottom:14}}>Upgrade your plan</div>
                    <div className="st-plan-grid">
                      {PLANS.filter(p => p.price > currentPlan.price).map(p => (
                        <div key={p.id} className="st-plan-up">
                          <div className="st-plan-up-hd">
                            <div className="st-plan-up-name">{p.name}</div>
                            <div className="st-plan-up-price">₹{p.price.toLocaleString('en-IN')}<span>/mo + GST</span></div>
                          </div>
                          <div className="st-plan-up-sub">{p.userLimit == null ? 'Unlimited members' : `Up to ${p.userLimit} members`}</div>
                          <ul className="st-plan-up-feats">
                            {p.features.map(f => <li key={f}>{f}</li>)}
                          </ul>
                          <button className="st-save-btn st-plan-up-btn" onClick={() => requestUpgrade(p)}>Request upgrade</button>
                        </div>
                      ))}
                    </div>
                    <div className="st-help-text" style={{fontSize:12,marginTop:14,opacity:.6}}>
                      Upgrade requests are confirmed personally on WhatsApp. Your plan changes as soon as we confirm, nothing is charged automatically.
                    </div>
                  </div>
                )}
                {upgradeMsg && <div className="st-msg st-msg-ok" style={{marginTop:12}}>{upgradeMsg}</div>}

                <div className="st-save-row" style={{marginTop:20}}>
                  <button className="st-cancel-btn" onClick={()=>window.open(`https://wa.me/${UPGRADE_WHATSAPP}?text=${encodeURIComponent('Hi Ryzenforge, I would like to discuss my RyzenOS subscription.')}`,'_blank','noopener')}>Contact us about billing</button>
                </div>
              </div>
            )}

            {tab==='Roles' && (
              <div className="st-section">
                <div className="st-sec-title">Roles & Permissions</div>
                <div className="st-roles-info">Roles are assigned from the Team page. Each role controls what a member can see and do.</div>
                {[
                  {role:'Super Admin',color:'#F5A623',perms:['Full access to all modules','Add/edit/delete leads, clients, team','Manage billing and settings','View all reports and analytics']},
                  {role:'Admin',color:'#3B82F6',perms:['View all modules','Add/edit leads, clients, projects','Cannot delete team members','Cannot access billing']},
                  {role:'User',color:'#22C55E',perms:['Access employee portal only','Mark own attendance','View assigned tasks','Cannot access CRM data']},
                ].map(r=>(
                  <div key={r.role} className="st-role-card">
                    <div className="st-role-hd"><div className="st-role-name" style={{color:r.color}}>{r.role}</div></div>
                    <ul className="st-role-perms">{r.perms.map(p=><li key={p} className="st-role-perm">{p}</li>)}</ul>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
